import { useNavigate } from 'react-router-dom';
import { useAuthorizationStore } from '@/stores/authorization';
import { canUseMultiEstablishmentContext, useAuthStore } from '@/stores/auth';
import { motion } from 'framer-motion';
import { Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MdAdminPanelSettings, MdLocalHospital } from 'react-icons/md';
import { FaBuilding } from 'react-icons/fa6';

const MODULE_ICONS: Record<string, React.ReactNode> = {
  administrative: <MdAdminPanelSettings className="w-10 h-10" />,
  establishment: <MdLocalHospital className="w-10 h-10" />,
  medical_company: <FaBuilding className="w-10 h-10" />,
  medical_management: <Activity className="w-10 h-10" />,
};

const MODULE_LABEL_KEYS: Record<string, string> = {
  administrative: 'module_selector.modules.administrative',
  establishment: 'module_selector.modules.establishment',
  medical_company: 'module_selector.modules.medical_company',
  medical_management: 'module_selector.modules.medical_management',
};

const MODULES_REQUIRING_ESTABLISHMENT_CONTEXT = [
  'establishment',
  'medical_company',
];

export default function ModuleGatewayPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const payload = useAuthorizationStore((s) => s.payload);
  const setSelectedModule = useAuthorizationStore((s) => s.setSelectedModule);
  const user = useAuthStore((s) => s.user);
  const requireContextSelection = useAuthStore(
    (s) => s.requireContextSelection,
  );
  const modules = payload?.modules ?? [];

  const handleSelect = (moduleKey: string, defaultRoute: string) => {
    setSelectedModule(moduleKey);

    const requiresEstablishmentContext =
      MODULES_REQUIRING_ESTABLISHMENT_CONTEXT.includes(moduleKey);
    const canUseContext = canUseMultiEstablishmentContext(user);

    if (
      requiresEstablishmentContext &&
      canUseContext
    ) {
      requireContextSelection(defaultRoute);
      return;
    }

    navigate(defaultRoute);
  };

  const getModuleLabel = (moduleKey: string) => {
    const translationKey = MODULE_LABEL_KEYS[moduleKey];
    return translationKey ? t(translationKey) : moduleKey;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10 text-center"
      >
        <h1 className="text-3xl font-bold text-foreground">
          {t('module_gateway.title')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {t('module_gateway.description')}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-3xl w-full">
        {modules.map((mod, i) => (
          <motion.button
            key={mod.key}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.08 }}
            onClick={() => handleSelect(mod.key, mod.default_route)}
            className="flex flex-col items-center justify-center gap-4 p-8 rounded-2xl border border-border bg-card hover:bg-accent hover:border-primary transition-all shadow-sm hover:shadow-md cursor-pointer"
          >
            <span className="text-primary">
              {MODULE_ICONS[mod.key] ?? <MdAdminPanelSettings className="w-10 h-10" />}
            </span>
            <span className="text-lg font-semibold text-card-foreground">
              {getModuleLabel(mod.key)}
            </span>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
